/**
 * Portal-Grundfunktionen per Tastatur.
 * Standard: Alt+S Suche, Alt+A Akten, Alt+D Diktat, Alt+N Anlegen, Escape Zurück.
 * Strg/Cmd+K (Schnellzugriff) und `/` (ARKO-Fokus) bleiben, bis sie nicht umgelegt werden.
 * Eine Belegung ist Taste plus Alt, Strg, Umschalt, Befehl — Alt ist keine Pflicht.
 * F5, Strg+R/W/T/N/L, Alt+D, Alt+Links und Tab behält der Browser.
 * Trifft das Event die Seite, schluckt die Capture-Phase es (auch F1).
 */

export type PortalNavShortcut = 'suche' | 'akten' | 'diktat' | 'neu';
export type PortalShortcutCoreId = PortalNavShortcut | 'zurueck';
/** Vorhandene Reiter und die Glocke. Keine Ketten, keine Makros. */
export type PortalShortcutExtraId =
  | 'arko'
  | 'dokumente'
  | 'kalender'
  | 'kanzlei_chat'
  | 'gedaechtnis'
  | 'fallanalyse'
  | 'vertrag'
  | 'bausteine'
  | 'arbeitsplatz'
  | 'kanzlei_zugaenge'
  | 'glocke';
export type PortalShortcutId = PortalShortcutCoreId | PortalShortcutExtraId;

export type PortalChord = {
  key: string;
  alt: boolean;
  shift: boolean;
  ctrl: boolean;
  meta: boolean;
};

export type PortalShortcutMap = Record<PortalShortcutCoreId, PortalChord> & Record<PortalShortcutExtraId, PortalChord | null>;

export const PORTAL_SHORTCUT_CORE_IDS: readonly PortalShortcutCoreId[] = [
  'suche',
  'akten',
  'diktat',
  'neu',
  'zurueck',
];

export const PORTAL_SHORTCUT_EXTRA_IDS: readonly PortalShortcutExtraId[] = [
  'arko',
  'dokumente',
  'kalender',
  'kanzlei_chat',
  'gedaechtnis',
  'fallanalyse',
  'vertrag',
  'bausteine',
  'arbeitsplatz',
  'kanzlei_zugaenge',
  'glocke',
];

export const PORTAL_SHORTCUT_IDS: readonly PortalShortcutId[] = [
  ...PORTAL_SHORTCUT_CORE_IDS,
  ...PORTAL_SHORTCUT_EXTRA_IDS,
];

export const PORTAL_SHORTCUT_LABEL: Record<PortalShortcutId, string> = {
  suche: 'Suche',
  akten: 'Akten',
  diktat: 'Diktat',
  neu: 'Anlegen',
  zurueck: 'Alle Akten / Abbrechen',
  arko: 'ARKO',
  dokumente: 'Dokumente',
  kalender: 'Kalender',
  kanzlei_chat: 'Kanzlei-Chat',
  gedaechtnis: 'Gedächtnis',
  fallanalyse: 'Fallanalyse',
  vertrag: 'Vertrag',
  bausteine: 'Bausteine',
  arbeitsplatz: 'Arbeitsplatz',
  kanzlei_zugaenge: 'Kanzlei & Zugänge',
  glocke: 'Glocke',
};

export const PORTAL_SHORTCUT_CHANGED = 'arkorbit:portal-shortcuts';

const ESCAPE_CHORD: PortalChord = {
  key: 'Escape',
  alt: false,
  shift: false,
  ctrl: false,
  meta: false,
};

export const PORTAL_SHORTCUT_DEFAULTS: PortalShortcutMap = {
  suche: { key: 's', alt: true, shift: false, ctrl: false, meta: false },
  akten: { key: 'a', alt: true, shift: false, ctrl: false, meta: false },
  diktat: { key: 'd', alt: true, shift: false, ctrl: false, meta: false },
  neu: { key: 'n', alt: true, shift: false, ctrl: false, meta: false },
  zurueck: { ...ESCAPE_CHORD },
  arko: null,
  dokumente: null,
  kalender: null,
  kanzlei_chat: null,
  gedaechtnis: null,
  fallanalyse: null,
  vertrag: null,
  bausteine: null,
  arbeitsplatz: null,
  kanzlei_zugaenge: null,
  glocke: null,
};

type ShortcutKeyEvent = {
  key: string;
  altKey: boolean;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  repeat?: boolean;
};

export type PortalShortcutOpenView =
  | 'suche'
  | 'akten'
  | 'diktat'
  | 'chat'
  | 'dokumente'
  | 'kalender'
  | 'matrix'
  | 'gedaechtnis'
  | 'litigation'
  | 'contract_generator'
  | 'bausteine'
  | 'home'
  | 'kanzlei_zugaenge';

export type PortalShortcutResolution =
  | { kind: 'none' }
  | { kind: 'navigate'; view: PortalShortcutOpenView }
  | { kind: 'press'; button: 'neu' | 'zurueck' }
  | { kind: 'glocke' };

type ShortcutStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

let captureDepth = 0;

export function portalShortcutCaptureBegin(): void {
  captureDepth += 1;
}

export function portalShortcutCaptureEnd(): void {
  captureDepth = Math.max(0, captureDepth - 1);
}

/** Während der Anwalt eine Taste setzt, keine Grundfunktion auslösen. */
export function isPortalShortcutCaptureActive(): boolean {
  return captureDepth > 0;
}

export function portalNavShortcutId(view: string): 'suche' | 'akten' | 'diktat' | null {
  if (view === 'suche' || view === 'akten' || view === 'diktat') return view;
  return null;
}

function cloneChord(chord: PortalChord | null): PortalChord | null {
  return chord ? { ...chord } : null;
}

function clonePortalShortcutMap(map: PortalShortcutMap = PORTAL_SHORTCUT_DEFAULTS): PortalShortcutMap {
  return {
    suche: { ...map.suche },
    akten: { ...map.akten },
    diktat: { ...map.diktat },
    neu: { ...map.neu },
    zurueck: { ...map.zurueck },
    arko: cloneChord(map.arko),
    dokumente: cloneChord(map.dokumente),
    kalender: cloneChord(map.kalender),
    kanzlei_chat: cloneChord(map.kanzlei_chat),
    gedaechtnis: cloneChord(map.gedaechtnis),
    fallanalyse: cloneChord(map.fallanalyse),
    vertrag: cloneChord(map.vertrag),
    bausteine: cloneChord(map.bausteine),
    arbeitsplatz: cloneChord(map.arbeitsplatz),
    kanzlei_zugaenge: cloneChord(map.kanzlei_zugaenge),
    glocke: cloneChord(map.glocke),
  };
}

function portalShortcutChordsEqual(a: PortalChord, b: PortalChord): boolean {
  return a.key === b.key && a.alt === b.alt && a.shift === b.shift && a.ctrl === b.ctrl && a.meta === b.meta;
}

function isBindableKey(key: string): boolean {
  if (key === 'Escape' || key === 'Backspace' || key === 'Delete' || key === 'Enter' || key === 'Tab') return true;
  if (key === 'Home' || key === 'End' || key === 'PageUp' || key === 'PageDown') return true;
  if (key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight') return true;
  if (/^F([1-9]|1[0-2])$/.test(key)) return true;
  return key.length === 1;
}

const KEY_LABEL: Record<string, string> = {
  Escape: 'Esc',
  ArrowLeft: 'Links',
  ArrowRight: 'Rechts',
  ArrowUp: 'Auf',
  ArrowDown: 'Ab',
  Backspace: 'Rück',
  Delete: 'Entf',
  PageUp: 'Bild auf',
  PageDown: 'Bild ab',
  Home: 'Pos1',
  End: 'Ende',
};

/** Taste plus Kombination. Nur-Modifier ohne Taste zählt nicht. */
export function portalChordFromKeyboardEvent(event: ShortcutKeyEvent): PortalChord | null {
  if (event.repeat) return null;
  const raw = event.key;
  if (!raw || raw === 'Alt' || raw === 'Shift' || raw === 'Control' || raw === 'Meta' || raw === 'AltGraph') {
    return null;
  }
  const key = raw.length === 1 ? raw.toLowerCase() : raw === 'Esc' ? 'Escape' : raw;
  if (!isBindableKey(key)) return null;
  return {
    key,
    alt: event.altKey,
    shift: event.shiftKey,
    ctrl: event.ctrlKey,
    meta: event.metaKey,
  };
}

/** Reload, Tab, neues Fenster, Adresszeile. F1 gehört nicht dazu. */
export function portalShortcutBrowserKeepsChord(chord: PortalChord): boolean {
  const command = chord.ctrl || chord.meta;
  if (chord.key === 'F5' || chord.key === 'Tab') return true;
  if (command && !chord.alt && ['r', 'w', 't', 'n', 'l'].includes(chord.key)) return true;
  if (chord.alt && !chord.ctrl && !chord.meta && (chord.key === 'd' || chord.key === 'ArrowLeft')) return true;
  return false;
}

function portalShortcutPortalKeepsChord(chord: PortalChord): boolean {
  if (chord.key === '/' && !chord.alt && !chord.shift && !chord.ctrl && !chord.meta) return true;
  if ((chord.ctrl || chord.meta) && chord.key === 'k') return true;
  return false;
}

export function formatPortalShortcutHint(chord: PortalChord): string {
  const parts: string[] = [];
  if (chord.ctrl) parts.push('Strg');
  if (chord.alt) parts.push('Alt');
  if (chord.shift) parts.push('Umschalt');
  if (chord.meta) parts.push('Befehl');
  parts.push(KEY_LABEL[chord.key] ?? (chord.key.length === 1 ? chord.key.toUpperCase() : chord.key));
  return parts.join('+');
}

export function formatPortalShortcutAria(chord: PortalChord): string {
  return formatPortalShortcutHint(chord);
}

function portalShortcutStorageKey(seatId: string): string {
  const safe = String(seatId).replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 80);
  return `arkorbit.portal.shortcuts.v1.${safe}`;
}

function parseStoredChord(value: unknown): PortalChord | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (typeof row.key !== 'string') return null;
  const key = row.key.length === 1 ? row.key.toLowerCase() : row.key;
  if (!isBindableKey(key) || key.length > 16) return null;
  const chord: PortalChord = {
    key,
    alt: row.alt === true,
    shift: row.shift === true,
    ctrl: row.ctrl === true,
    meta: row.meta === true,
  };
  if (portalShortcutBrowserKeepsChord(chord) || portalShortcutPortalKeepsChord(chord)) return null;
  return chord;
}

export type PortalShortcutAssignResult =
  | { ok: true; map: PortalShortcutMap }
  | { ok: false; reason: 'taken'; takenBy: PortalShortcutId }
  | { ok: false; reason: 'reserved'; reserved: 'browser' | 'portal' };

export function applyPortalShortcutAssignment(
  map: PortalShortcutMap,
  id: PortalShortcutId,
  chord: PortalChord,
): PortalShortcutAssignResult {
  if (portalShortcutBrowserKeepsChord(chord)) return { ok: false, reason: 'reserved', reserved: 'browser' };
  if (portalShortcutPortalKeepsChord(chord)) return { ok: false, reason: 'reserved', reserved: 'portal' };
  for (const other of PORTAL_SHORTCUT_IDS) {
    const existing = map[other];
    if (other !== id && existing && portalShortcutChordsEqual(existing, chord)) {
      return { ok: false, reason: 'taken', takenBy: other };
    }
  }
  return {
    ok: true,
    map: {
      ...clonePortalShortcutMap(map),
      [id]: { key: chord.key, alt: chord.alt, shift: chord.shift, ctrl: chord.ctrl, meta: chord.meta },
    },
  };
}

export function loadPortalShortcutMap(storage: Pick<Storage, 'getItem'> | null, seatId: string): PortalShortcutMap {
  const map = clonePortalShortcutMap();
  if (!storage || !seatId.trim()) return map;
  const raw = storage.getItem(portalShortcutStorageKey(seatId));
  if (!raw) return map;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return map;
  }
  if (!parsed || typeof parsed !== 'object') return map;
  const source = parsed as Record<string, unknown>;
  let next = map;
  for (const id of PORTAL_SHORTCUT_IDS) {
    const chord = parseStoredChord(source[id]);
    if (!chord) continue;
    const applied = applyPortalShortcutAssignment(next, id, chord);
    if (applied.ok) next = applied.map;
  }
  return next;
}

function dispatchShortcutChange(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PORTAL_SHORTCUT_CHANGED));
}

/** Nur gesetzte Tasten. Leere Aktionen bleiben weg. Keine Namen, keine Ketten, keine Mandanten-PII. */
export function savePortalShortcutMap(storage: ShortcutStorage | null, seatId: string, map: PortalShortcutMap): void {
  if (!storage || !seatId.trim()) return;
  const payload: Record<string, { key: string; alt: boolean; shift: boolean; ctrl: boolean; meta: boolean }> = {};
  for (const id of PORTAL_SHORTCUT_IDS) {
    const chord = map[id];
    if (!chord) continue;
    payload[id] = {
      key: chord.key,
      alt: chord.alt,
      shift: chord.shift,
      ctrl: chord.ctrl,
      meta: chord.meta,
    };
  }
  storage.setItem(portalShortcutStorageKey(seatId), JSON.stringify(payload));
  dispatchShortcutChange();
}

export function resetPortalShortcutMap(storage: ShortcutStorage | null, seatId: string): PortalShortcutMap {
  if (storage && seatId.trim()) storage.removeItem(portalShortcutStorageKey(seatId));
  dispatchShortcutChange();
  return clonePortalShortcutMap();
}

function matchPortalShortcutId(event: ShortcutKeyEvent, map: PortalShortcutMap): PortalShortcutId | null {
  if (event.repeat) return null;
  const chord = portalChordFromKeyboardEvent(event);
  if (!chord || portalShortcutPortalKeepsChord(chord)) return null;
  for (const id of PORTAL_SHORTCUT_IDS) {
    const existing = map[id];
    if (existing && portalShortcutChordsEqual(existing, chord)) return id;
  }
  return null;
}

function resolutionForShortcut(id: PortalShortcutId): PortalShortcutResolution {
  switch (id) {
    case 'suche':
    case 'akten':
    case 'diktat':
      return { kind: 'navigate', view: id };
    case 'neu':
    case 'zurueck':
      return { kind: 'press', button: id };
    case 'arko':
      return { kind: 'navigate', view: 'chat' };
    case 'dokumente':
      return { kind: 'navigate', view: 'dokumente' };
    case 'kalender':
      return { kind: 'navigate', view: 'kalender' };
    case 'kanzlei_chat':
      return { kind: 'navigate', view: 'matrix' };
    case 'gedaechtnis':
      return { kind: 'navigate', view: 'gedaechtnis' };
    case 'fallanalyse':
      return { kind: 'navigate', view: 'litigation' };
    case 'vertrag':
      return { kind: 'navigate', view: 'contract_generator' };
    case 'bausteine':
      return { kind: 'navigate', view: 'bausteine' };
    case 'arbeitsplatz':
      return { kind: 'navigate', view: 'home' };
    case 'kanzlei_zugaenge':
      return { kind: 'navigate', view: 'kanzlei_zugaenge' };
    case 'glocke':
      return { kind: 'glocke' };
  }
}

/**
 * Welche Grundfunktion dieser Tastendruck auslöst.
 * In einem Feld: nichts, außer der aktuell belegten Taste für Alle Akten / Abbrechen
 * im Titel „Neue Akte“.
 */
export function resolvePortalShortcut(
  event: ShortcutKeyEvent,
  options: { map: PortalShortcutMap; typing: boolean; zurueckInTitle?: boolean },
): PortalShortcutResolution {
  if (event.repeat) return { kind: 'none' };
  const id = matchPortalShortcutId(event, options.map);
  if (!id) return { kind: 'none' };
  if (options.typing) {
    if (id === 'zurueck' && options.zurueckInTitle) return { kind: 'press', button: 'zurueck' };
    return { kind: 'none' };
  }
  return resolutionForShortcut(id);
}

export function swallowPortalShortcutEvent(event: { preventDefault: () => void; stopPropagation: () => void }): void {
  event.preventDefault();
  event.stopPropagation();
}

/** Trifft die belegte Taste die Seite, preventDefault — auch bei F1. */
export function dispatchPortalShortcutKey(
  event: ShortcutKeyEvent & { preventDefault: () => void; stopPropagation: () => void },
  options: { map: PortalShortcutMap; typing: boolean; zurueckInTitle?: boolean },
): PortalShortcutResolution {
  const action = resolvePortalShortcut(event, options);
  if (action.kind !== 'none') swallowPortalShortcutEvent(event);
  return action;
}

/** Tippen in einem Feld — neue Kürzel lösen dort nicht aus. */
export function isPortalShortcutTypingTarget(target: EventTarget | null): boolean {
  if (!target || typeof target !== 'object') return false;
  const el = target as { tagName?: string; isContentEditable?: boolean };
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}

/**
 * Escape bleibt das bestehende Schließen.
 * Im Formular „Neue Akte“ darf Esc auch aus dem Titel-Feld abbrechen,
 * weil der Abbrechen-Knopf in demselben Formular liegt.
 * Im Chat (Rahmen) gilt das nicht — dort ist Esc kein „Alle Akten“.
 */
export function portalShortcutZurueckFromTypingTarget(target: EventTarget | null): boolean {
  if (!target || typeof target !== 'object' || !('closest' in target)) return false;
  const el = target as { closest: (sel: string) => unknown };
  return Boolean(el.closest('[data-portal-shortcut-scope="zurueck"]'));
}

/** Klickt den sichtbaren, nicht deaktivierten Knopf. Kein Knopf = keine Aktion. */
export function activatePortalShortcutButton(kind: 'neu' | 'zurueck'): boolean {
  if (typeof document === 'undefined') return false;
  const nodes = document.querySelectorAll<HTMLButtonElement>(`button[data-portal-shortcut="${kind}"]`);
  for (const node of nodes) {
    if (node.disabled) continue;
    if (node.getAttribute('aria-hidden') === 'true') continue;
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') continue;
    node.click();
    return true;
  }
  return false;
}
